"use client";

import { useEffect, useState } from "react";
import { ArrowRight, Sparkles, X } from "lucide-react";

import { useFeatureFlags } from "@/hooks/useFeatureFlags";
import { useFeatureAnnouncement } from "@/hooks/useFeatureAnnouncement";

/**
 * Bottom-of-screen banner that points admin-or-higher users at the latest
 * "What's New" post on the marketing site. It appears once per post (tracked
 * in localStorage by slug) and only when that post is less than a week old —
 * both enforced upstream by /api/whats-new/current. Regular users never fetch.
 */
export default function WhatsNewBanner() {
  const { isAdmin, isSuperUser, loading } = useFeatureFlags();
  const isAdminOrHigher = isAdmin || isSuperUser;
  const [post, setPost] = useState(null);

  useEffect(() => {
    if (loading || !isAdminOrHigher) return;

    let cancelled = false;
    (async () => {
      try {
        const res = await fetch("/api/whats-new/current");
        if (!res.ok) return;
        const data = await res.json();
        if (!cancelled && data?.post?.slug) setPost(data.post);
      } catch {
        // Silent — a missing banner is never worth surfacing to the user.
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [loading, isAdminOrHigher]);

  if (!isAdminOrHigher || !post) return null;

  // Keyed on slug so each new post is a fresh announcement the user sees once.
  return <WhatsNewBannerCard key={post.slug} post={post} />;
}

function WhatsNewBannerCard({ post }) {
  const { showAnnouncement, markAsSeen } = useFeatureAnnouncement(
    `whats_new_${post.slug}`,
  );
  const [entered, setEntered] = useState(false);

  useEffect(() => {
    if (!showAnnouncement) return;
    const id = requestAnimationFrame(() => setEntered(true));
    return () => cancelAnimationFrame(id);
  }, [showAnnouncement]);

  if (!showAnnouncement) return null;

  return (
    <div
      role="region"
      aria-label="What's new"
      className={`fixed inset-x-0 bottom-0 z-50 flex justify-center px-4 pb-4 transition-all duration-500 ease-out ${
        entered ? "translate-y-0 opacity-100" : "translate-y-6 opacity-0"
      }`}
    >
      <div className="relative flex w-full max-w-2xl items-center gap-4 overflow-hidden rounded-2xl border border-gray-200 bg-white p-4 shadow-[0_20px_50px_-24px_rgba(15,23,42,0.45)] ring-1 ring-gray-900/5 sm:gap-5 sm:p-5">
        {/* Coral hairline accent, clipped to the rounded corners. */}
        <span
          aria-hidden
          className="pointer-events-none absolute inset-x-0 top-0 h-px bg-brand-600"
        />

        {/* Coral icon tile (brand accent). */}
        <span className="hidden h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-brand-50 text-brand-600 ring-1 ring-brand-200 sm:flex">
          <Sparkles className="h-5 w-5" strokeWidth={1.75} />
        </span>

        <div className="min-w-0 flex-1">
          <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-brand-600">
            What&apos;s new
          </p>
          <h2 className="mt-1 truncate text-sm font-semibold tracking-tight text-gray-900">
            {post.title}
          </h2>
          {post.excerpt ? (
            <p className="mt-0.5 line-clamp-1 text-sm text-gray-500">
              {post.excerpt}
            </p>
          ) : null}
        </div>

        <div className="flex shrink-0 items-center gap-1.5 sm:gap-2">
          <a
            href={post.url}
            target="_blank"
            rel="noopener noreferrer"
            onClick={markAsSeen}
            className="inline-flex items-center gap-1.5 rounded-md bg-blue-600 px-3.5 py-2 text-sm font-semibold text-white transition-colors hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-400 focus:ring-offset-2"
          >
            View update
            <ArrowRight className="h-3.5 w-3.5" />
          </a>
          <button
            type="button"
            onClick={markAsSeen}
            aria-label="Dismiss"
            className="rounded-md p-2 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  );
}
